import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaPhoneAlt, FaLine, FaEnvelope, FaMapMarkerAlt, FaClock, FaCopy } from "react-icons/fa";

/* ========= THEME ========= */
const THEME = {
  fontFamily:
    "'Prompt', system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial, 'Noto Sans Thai', sans-serif",
  pageBg: "#0b1220",
  textOnDark: "rgba(255,255,255,.92)",
  glass: "rgba(255,255,255,.08)",
  glassStroke: "rgba(255,255,255,.18)",
  p1: "#60a5fa",
  p2: "#22d3ee",
  p3: "#34d399",
  accent: "#d4af37",
};

/* ========= CONTENT CONFIG ========= */
const COMPANY = {
  name: "Thai Tax & Law",
  address: "677/4 ซอย ลาดพร้าว 48 แขวงสามเสนนอก เขตห้วยขวาง กรุงเทพมหานคร 10310",
  phone: "02-276-9968",
  phoneHref: "tel:022769968",
  email: "oaoffice@ornthai.com",
  lineId: "@443namoj",
  lineHref: "https://line.me/R/ti/p/@443namoj",
  hours: [
    { label: "จันทร์–ศุกร์", value: "08:30–17:30" },
  ],
  map: { lat: 13.826, lng: 100.560, zoom: 15 },
};

const HERO_MEDIA = {
  image: "https://images.unsplash.com/photo-1423666639041-f56000c27a9a?q=80&w=1174&auto=format&fit=crop",
};

export default function ContactPage() {
  const { t } = useTranslation();
  const heroRef = useRef(null);
  const [toast, setToast] = useState("");

  const hours = [
    { label: t("contact_page.hours_labels.mon_fri"), value: "08:30–17:30" },
  ];

  useEffect(() => {
    const onScroll = () => {
      const h = heroRef.current;
      if (!h) return;
      const y = window.scrollY;
      h.style.setProperty("--parallax", Math.min(y * 0.22, 120) + "px");
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setToast(t("contact_page.copy_success"));
      setTimeout(() => setToast(""), 2000);
    } catch {
      setToast(t("contact_page.copy_failed"));
    }
  };

  return (
    <div className="contact-page" style={{ fontFamily: THEME.fontFamily }}>
      <Style />
      {toast && <div className="toast-notify">{toast}</div>}

      {/* HERO SECTION */}
      <header className="hero" ref={heroRef}>
        <div className="hero-media-wrap">
          <img className="hero-media" src={HERO_MEDIA.image} alt="Contact background" />
        </div>
        <div className="hero-overlay" />
        <div className="hero-content">
          {/* Breadcrumbs removed as per request */}
          <h1 className="hero-main-title">{t("contact_page.hero_title")}</h1>
          <p className="hero-description" dangerouslySetInnerHTML={{ __html: t("contact_page.hero_desc") }} />
          
          <div className="contact-pills-wrap">
            <div className="contact-pill phone">
              <FaPhoneAlt /> {COMPANY.phone}
            </div>
            <div className="contact-pill email">
              <FaEnvelope /> {COMPANY.email}
            </div>
            <a href={COMPANY.lineHref} target="_blank" rel="noreferrer" className="contact-pill line">
              <FaLine size={20} /> LINE {COMPANY.lineId}
            </a>
          </div>
        </div>
        <div className="hero-curve" />
      </header>

      {/* MAIN CONTENT & MAP */}
      <main className="contact-main">
        <div className="info-grid container">
          <div className="info-card reveal-up">
            <div className="card-inner">
              <div className="icon-box"><FaMapMarkerAlt /></div>
              <h3>{t("contact_page.office_location")}</h3>
              <p>{COMPANY.address}</p>
              <button 
                onClick={() => handleCopy(COMPANY.address)}
                className="copy-btn"
              >
                <FaCopy /> {t("contact_page.copy_address")}
              </button>
            </div>
          </div>

          <div className="info-card reveal-up">
            <div className="card-inner">
              <div className="icon-box"><FaClock /></div>
              <h3>{t("contact_page.working_hours")}</h3>
              <ul className="hours-list">
                {hours.map((h, i) => (
                  <li key={i}>
                    <span>{h.label}</span>
                    <strong>{h.value}</strong>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* REFINED MAP SECTION (2 columns) */}
        <section className="map-section container">
          <div className="map-container">
            <div className="map-header">
              <FaMapMarkerAlt /> สำนักงานอรอนงค์การบัญชีและสอบบัญชี
            </div>
            <div className="map-frame-box">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2987.8303049194446!2d100.58491317828334!3d13.794740826538185!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x30e29d23e23da677%3A0x14a7d4d171d5a50c!2z4Liq4Liz4LiZ4Lix4LiB4LiH4Liy4LiZ4Lit4Lij4Lit4LiZ4LiH4LiE4LmM4LiB4Liy4Lij4Lia4Lix4LiN4LiK4Li14LmB4Lil4Liw4Liq4Lit4Lia4Lia4Lix4LiN4LiK4Li1!5e0!3m2!1sth!2sth!4v1753150019956!5m2!1sth!2sth"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Google Map - สำนักงานอรอนงค์การบัญชีและสอบบัญชี"
              ></iframe>
            </div>
            <div className="map-footer">
              {t("contact_page.map_footer")}
            </div>
          </div>

          <div className="map-container">
            <div className="map-header">
              <FaMapMarkerAlt /> บริษัท ไทย แทคซ์ แอนด์ ลอว์ จำกัด
            </div>
            <div className="map-frame-box">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d663.4456850066628!2d100.58470597331035!3d13.795076855271867!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x30e29d0067c574dd%3A0xc6ebce9fd46382c3!2z4Lia4Lij4Li04Lip4Lix4LiXIOC5hOC4l-C4oiDguYHguJfguITguIvguYwg4LmB4Lit4LiZ4LiU4LmMIOC4peC4reC4p-C5jCDguIjguLPguIHguLHguJQ!5e0!3m2!1sth!2sth!4v1780116214406!5m2!1sth!2sth"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Google Map - บริษัท ไทย แทคซ์ แอนด์ ลอว์ จำกัด"
              ></iframe>
            </div>
            <div className="map-footer">
              {t("contact_page.map_footer")}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

function Style() {
  return (
    <style>{`
      .contact-page {
        background: ${THEME.pageBg};
        color: #fff;
        min-height: 100vh;
      }
      .container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 0 20px;
      }

      /* HERO */
      .hero {
        position: relative;
        height: 65vh;
        display: flex;
        align-items: center;
        overflow: hidden;
      }
      .hero-media-wrap {
        position: absolute;
        inset: 0;
        z-index: 0;
        transform: translateY(var(--parallax));
      }
      .hero-media {
        width: 100%;
        height: 100%;
        object-fit: cover;
        filter: brightness(0.4) contrast(1.1);
      }
      .hero-overlay {
        position: absolute;
        inset: 0;
        background: linear-gradient(to bottom, transparent 40%, ${THEME.pageBg} 100%);
        z-index: 1;
      }
      .hero-content {
        position: relative;
        z-index: 2;
        max-width: 1000px;
        margin: 0 auto;
        padding: 0 20px;
        text-align: center;
      }
      .breadcrumbs-nav {
        font-size: 14px;
        opacity: 0.7;
        margin-bottom: 15px;
        letter-spacing: 1px;
      }
      .breadcrumbs-nav span { margin: 0 10px; }
      .hero-main-title {
        font-size: clamp(2.5rem, 5vw, 4rem);
        font-weight: 800;
        margin-bottom: 20px;
        letter-spacing: -0.02em;
        color: #fff;
      }
      .hero-description {
        font-size: 1.2rem;
        max-width: 700px;
        margin: 0 auto 40px;
        opacity: 0.9;
        line-height: 1.7;
      }

      /* PILLS */
      .contact-pills-wrap {
        display: flex;
        justify-content: center;
        gap: 20px;
        flex-wrap: wrap;
      }
      .contact-pill {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 14px 28px;
        border-radius: 50px;
        text-decoration: none;
        font-weight: 700;
        font-size: 16px;
        transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        border: 1px solid rgba(255,255,255,0.1);
        cursor: default;
      }
      a.contact-pill {
        cursor: pointer;
      }
      a.contact-pill:hover {
        transform: translateY(-5px);
        box-shadow: 0 10px 20px rgba(0,0,0,0.3);
      }
      .contact-pill.phone {
        background: ${THEME.accent};
        color: #000;
      }
      .contact-pill.email {
        background: rgba(255,255,255,0.1);
        color: #fff;
        border: 1px solid rgba(255,255,255,0.2);
      }
      .contact-pill.line {
        background: #06C755;
        color: #fff;
        border: none;
      }

      /* MAIN */
      .contact-main {
        padding: 60px 0 100px;
        position: relative;
        z-index: 5;
      }
      .info-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        gap: 30px;
        margin-bottom: 80px;
      }
      .info-card {
        background: rgba(255,255,255,0.03);
        border: 1px solid rgba(255,255,255,0.05);
        border-radius: 24px;
        padding: 40px;
        text-align: center;
        transition: all 0.4s ease;
      }
      .info-card:hover {
        background: rgba(255,255,255,0.05);
        border-color: ${THEME.accent};
      }
      .icon-box {
        font-size: 32px;
        color: ${THEME.accent};
        margin-bottom: 20px;
      }
      .info-card h3 {
        font-size: 24px;
        margin-bottom: 15px;
        color: #fff;
      }
      .info-card p {
        opacity: 0.8;
        line-height: 1.8;
        margin-bottom: 20px;
      }
      .copy-btn {
        background: none;
        border: 1px solid rgba(255,255,255,0.2);
        color: #fff;
        padding: 8px 16px;
        border-radius: 8px;
        cursor: pointer;
        font-size: 14px;
        display: inline-flex;
        align-items: center;
        gap: 8px;
      }
      .copy-btn:hover { background: rgba(255,255,255,0.1); }

      .hours-list {
        list-style: none;
        padding: 0;
      }
      .hours-list li {
        display: flex;
        justify-content: space-between;
        padding: 10px 0;
        border-bottom: 1px solid rgba(255,255,255,0.05);
      }
      .hours-list li strong { color: ${THEME.accent}; }

      /* MAP SECTION */
      .map-section {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 30px;
        align-items: start;
      }
      .map-container {
        width: 100%;
        background: #fff;
        border-radius: 30px;
        overflow: hidden;
        box-shadow: 0 30px 60px rgba(0,0,0,0.5);
        color: #000;
        border: 10px solid #fff;
      }
      .map-header {
        background: #f8fafc;
        padding: 15px 25px;
        font-weight: 700;
        display: flex;
        align-items: center;
        gap: 10px;
        color: #64748b;
        font-size: 14px;
      }
      .map-footer {
        background: #f8fafc;
        padding: 12px 25px;
        font-size: 12px;
        color: #94a3b8;
        text-align: center;
      }

      /* TOAST */
      .toast-notify {
        position: fixed;
        bottom: 30px;
        left: 50%;
        transform: translateX(-50%);
        background: #fff;
        color: #000;
        padding: 12px 24px;
        border-radius: 50px;
        z-index: 1000;
        font-weight: 700;
        box-shadow: 0 10px 30px rgba(0,0,0,0.3);
        animation: slideUp 0.3s ease;
      }
      @keyframes slideUp { from { bottom: 0; opacity: 0; } to { bottom: 30px; opacity: 1; } }

      @media (max-width: 768px) {
        .hero { height: auto; padding: 120px 0 60px; }
        .contact-pills-wrap { gap: 10px; }
        .contact-pill { padding: 12px 20px; width: 100%; justify-content: center; }
        .info-card { padding: 30px; }
        .map-section { grid-template-columns: 1fr; }
      }
    `}</style>
  );
}
